<script setup lang="ts">
import {onMounted, ref} from 'vue';
import useBackend from '../../composition/useBackend';
import {useRouter} from 'vue-router';
import {formatting} from '@hantera/portal-app';
import {FloatingPanel} from '@hantera/design-system';
import {PersonalAccessToken} from '@hantera/portal-contracts';

const emit = defineEmits<{
  (event: 'createNew'): void
}>()

const { user } = useBackend();
const { dateTime } = formatting;
const router = useRouter();

const activePats = ref<PersonalAccessToken[]>([]);
const expiredPats = ref<PersonalAccessToken[]>([]);
const revokeModal = ref<typeof FloatingPanel>();

onMounted(async () => {
  const pats = await user.getPersonalAccessTokens();

  activePats.value = pats.filter(pat => pat.active);
  expiredPats.value = pats.filter(pat => !pat.active);
});

function createNew() {
  emit('createNew')
}

const revokeKey = ref('');

function revoke(keyId: string) {
  revokeKey.value = keyId;
  revokeModal.value?.open();
}

async function revokeConfirm() {

  const revokedPat = await user.revokePersonalAccessToken(revokeKey.value);

  if (revokedPat) {
    activePats.value = activePats.value.filter(pat => pat.keyId !== revokeKey.value);
    expiredPats.value = [revokedPat, ...expiredPats.value];

    revokeKey.value = '';
    revokeModal.value?.close();
  }
}

</script>

<template>

  <h2>Personal Access Tokens (PAT)</h2>
  <p>
    Personal Access Tokens (PAT) are used to authenticate with the Hantera API. You can create a new PAT by clicking the button below.
  </p>

  <button class="btn-primary" @click="createNew()">Create PAT</button>

  <div v-if="activePats.length > 0">
    <h3>Active Tokens</h3>

    <table>
      <thead>
        <tr>
          <th>Description</th>
          <th>Issued At</th>
          <th>Expires At</th>
          <th>&nbsp;</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="pat in activePats" :key="pat.keyId">
          <td>{{ pat.description }}</td>
          <td>{{ dateTime(pat.issuedAt) }}</td>
          <td>{{ dateTime(pat.expiresAt) }}</td>
          <td>
            <button @click="revoke(pat.keyId)">Revoke</button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <div v-if="expiredPats.length > 0">
    <h3>Expired/Revoked Tokens</h3>

    <table>
      <thead>
        <tr>
          <th>Description</th>
          <th>Issued At</th>
          <th>Expired/Revoked At</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="pat in expiredPats" :key="pat.keyId">
          <td>{{ pat.description }}</td>
          <td>{{ dateTime(pat.issuedAt) }}</td>
          <td>{{ dateTime(pat.revokedAt || pat.expiresAt) }}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <FloatingPanel label="Confirm Revoke" ref="revokeModal" :showClose="true">
    <div class="revoke-modal">
      <p>Are you sure you want to revoke this Personal Access Token?</p>
      <div class="revoke-modal__actions">
        <button class="btn-primary" @click="revokeConfirm()">Revoke</button>
        <button @click="revokeModal?.close()">Cancel</button>
      </div>
    </div>
  </FloatingPanel>

</template>

<style>
.revoke-modal {
  margin: 1rem;
}

.revoke-modal__actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
}
</style>